import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BUSINESS_LAC, loginEndpoint } from "../../Constants/constants";
import { adminInfo } from "../../Redux/Slices/IsAdminSlice";
import { userInfo } from "../../Redux/Slices/UserSlice";
import { authenticate } from "./authenticate";

// Configurable variables
const LOGO_URL =
  "https://ziplyfiber.com/~/media/Residential/modules/images/ziply-svg-logo.ashx?h=77&iar=0&w=300&hash=E3A46862F989C124EF434048169AECAE";
const FORGOT_PASSWORD_URL = "https://resetpassword.nwestnetwork.com/PMUser/";
const PRIMARY_COLOR = "#0070e0";
const PRIMARY_HOVER_COLOR = "#005bb5";
const FOOTER_PADDING = "1rem";

const AdminLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const savedEmail = localStorage.getItem("userId");
    const savedPassword = localStorage.getItem("userPassword");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
    if (savedPassword) {
      setPassword(atob(savedPassword));
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (BUSINESS_LAC) handleBusinessLogin(e);
    else handleAdminLogin();
  };

  const handleBusinessLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Login details cannot be empty");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(loginEndpoint ?? "", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          loginID: email,
          password: btoa(password),
        }),
      });

      const data = await response.json();
      if (!response.ok || data?.content?.error) {
        setError(data?.content.error.description);
        setLoading(false);
        return;
      }

      // 🔹 Save token and basic user info
      if (data?.content.response.token) {
        localStorage.setItem("token", data.content.response.token);
      }
      if (data?.content.response.firstName) {
        localStorage.setItem(
          "userName",
          `${data.content.response.firstName} ${data?.content.response.lastName}`
        );
      }

      // 🔹 Save credentials if rememberMe checked
      if (rememberMe) {
        localStorage.setItem("userId", email);
        localStorage.setItem("userPassword", btoa(password));
      } else {
        localStorage.removeItem("userId");
        localStorage.removeItem("userPassword");
      }

      // TODO: handle deeplinking logic later
      // const deepLinkingValue = localStorage.getItem("impersonatedEmail") || "";
      // if (deepLinkingValue?.length > 1) {
      //   navigate(`/adminscreen?email=${deepLinkingValue}`);
      // } else {
      //   navigate("/adminscreen");
      // }

      // 🔹 Redirect to impersonation ; here you are mounting to enterprise portal
      window.location.replace("/lac/enterpriseportal/impersonation");
    } catch (err) {
      console.error(err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (errorCode: any) => {
    let errorMessage = "";
    if (errorCode === "AP206PRIDNTPR") {
      errorMessage = "Partner ID is not valid.";
    } else {
      errorMessage = "Oops... something went wrong. Please try again later.";
    }
    if (errorCode === "AP206UAF") {
      errorMessage = `Authentication failed. Please check your ID and Password`;
    }
    if (errorCode === "AP206UDNF") {
      errorMessage = `Permission denied...login to self service portal to request access.`;
    }
    return errorMessage;
  };

  const handleAdminLogin = async () => {
    if (error) {
      setError("");
    }
    if (!email || !password) {
      setError("Login details cannot be empty");
      return;
    }
    const item = { loginID: email, password: btoa(password) };

    try {
      setLoading(true);
      let result = await authenticate(item);

      if (result?.content?.error) {
        const { code } = result?.content?.error;
        setError(getErrorMessage(code));
      } else {
        var authentication = {
          isAuthenticated: result?.content?.response?.success?.code === 0,
        };
        dispatch(adminInfo(authentication));

        const deepLinkingValue = localStorage.getItem("impersonatedEmail") || "";

        if (deepLinkingValue?.length > 1) {
          navigate(`/adminscreen?email=${localStorage.getItem("impersonatedEmail")}`);
        } else {
          navigate("/adminscreen");
        }
        localStorage.setItem("userName", result.content.response.firstName);
        localStorage.setItem("token", result.content.response.token);

        const userDetails = {
          userToken: result.content.response.token,
        };
        dispatch(userInfo(userDetails));

        if (rememberMe) {
          localStorage.setItem("userId", item.loginID);
          localStorage.setItem("userPassword", item.password);
        } else {
          localStorage.removeItem("userId");
          localStorage.removeItem("userPassword");
        }
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  return (
    <Box
      sx={{
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        alignItems: "center",
        backgroundImage: `
          radial-gradient(circle at left top, rgba(0,173,255,0.3), transparent 50%),
          radial-gradient(circle at right bottom, rgba(100,255,100,0.3), transparent 50%)
        `,
      }}>
      {/* Main content wrapper */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <Box component="img" src={LOGO_URL} alt="Ziply Fiber" sx={{ height: 90, mb: 2 }} />

        <Paper
          elevation={8}
          sx={{
            p: 5,
            minWidth: 480,
            maxWidth: "90vw",
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 6,
          }}>
          <Typography variant="h6" fontWeight={700} align="center" gutterBottom>
            Impersonation
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" mb={2}>
            Welcome! Please sign in to continue.
          </Typography>

          <form style={{ width: "100%" }} onSubmit={handleSubmit} autoComplete="on">
            <TextField
              fullWidth
              margin="normal"
              label="Ziply Nwestnetwork ID"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              error={!!error && !email}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              error={!!error && !password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end">
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& input::-ms-reveal": {
                  display: "none",
                },
              }}
            />

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{
                  mt: 1,
                  mb: 1,
                  backgroundColor: "#ffe6e6",
                  p: 2,
                  borderRadius: 1,
                  textAlign: "center",
                }}>
                {error}
              </Typography>
            )}

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              mt={1}
              mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Remember me</Typography>}
              />
              <Button
                href={FORGOT_PASSWORD_URL}
                target="_blank"
                variant="text"
                size="small"
                sx={{ textTransform: "none", fontSize: 13, fontWeight: 400 }}>
                FORGOT PASSWORD?
              </Button>
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              disabled={loading}
              sx={{
                backgroundColor: PRIMARY_COLOR,
                borderRadius: 3,
                fontWeight: 700,
                fontSize: 16,
                mt: 1,
                boxShadow: "none",
                "&:hover": { backgroundColor: PRIMARY_HOVER_COLOR },
              }}>
              {loading ? "Please wait Logging in..." : "Log in"}
            </Button>
          </form>
        </Paper>
      </Box>

      {/* Sticky footer */}
      <Typography variant="caption" sx={{ pb: FOOTER_PADDING, textAlign: "center" }}>
        © {new Date().getFullYear()} Ziply Fiber. Ziplyᵀᴹ is a trademark of Northwest Fiber, LLC.
        All rights reserved.
      </Typography>
    </Box>
  );
};

export default AdminLogin;
